<template lang="html">
  <div id="reschedulingReceiverForm">
    <h1 class="header">授業振替依頼を承認／不承認する</h1>
    <div
      v-if="!reschedulingInfo"
      key="not-found"
    >
      <section class="section">
        <h1 class="section-description">指定された授業振替情報が見つかりません</h1>
      </section>
    </div>
    <div
      v-else-if="reschedulingInfo.receiver.id !== $store.state.userInfo.user_id"
      key="unauthorized"
    >
      <section class="section">
        <h1 class="section-description">授業振替情報を表示する権限がありません</h1>
      </section>
    </div>
    <div
      v-else-if="reschedulingInfo.status != 'pending'"
      key="got-result"
    >
      <section class="section">
        <h1 class="section-description">指定された授業振替情報は承認または非承認しております</h1>
      </section>
    </div>
    <div
      v-else-if="!checkExpireDate"
      key="expiration"
    >
      <section class="section">
        <h1 class="section-description">指定された授業振替情報の承認期限が過ぎております</h1>
      </section>
    </div>
    <div
      v-else
      key="receiver-form"
    >
      <section class="section">
        <h1 class="section-description">{{ senderDisplayName }}が以下の授業振替を依頼されています</h1>
        <div class="info">
          <h1 class="info-caption">対象の授業</h1>
          <div class="info-content">
            <p>日付：　{{formattedDate(reschedulingInfo.target_reservation.date)}}</p>
            <p>時間：　{{reschedulingInfo.target_reservation.start_time.slice(0, -3)}} 〜 {{reschedulingInfo.target_reservation.end_time.slice(0, -3)}}</p>
          </div>
        </div>
        <div class="info">
          <h1 class="info-caption">振替希望理由</h1>
          <p
            v-for="row in reschedulingInfo.reason.split('\n')"
            key="reason-row"
            class="info-content reason-row"
          >
            {{ row }}
          </p>
        </div>
      </section>

      <section class="section">
        <h1 class="section-description">振替を<font color="green">承認</font>する場合は<font color="green">候補日時のうちの1つを選択</font>してください</h1>
        <h1 class="section-description">振替を<font color="red">不承認</font>する場合は<font color="red">「振替を不承認する」にチェック</font>してください</h1>
        <notificationBox
          v-if="isCompletenessChecking && !isSelectionCompleted"
          key="warning-selection"
          class="notificationBox"
          type='warning'
          text="候補日時のうち1つを選択するか、「振替を不承認する」にチェックしてください"
        />
        <notificationBox
          v-else-if="isCompletenessChecking && isSelectionCompleted"
          key="ok-selection"
          class="notificationBox"
          type='ok'
          text="正しく入力されています"
        />
        <div>
          <input
            id="denying-checkbox"
            v-model="isDenying"
            type="checkbox"
          />
          <label for="denying-checkbox">振替を不承認する</label>
        </div>
        <div
          v-if="!isDenying"
          key="select-datetime-area"
          id="select-datetime-area"
        >
          <div
            :class="{'info-inline-block': !isNarrowMode}"
            class="info"
          >
            <h1 class="info-caption">元の授業</h1>
            <div class="info-content">
              <p>日付：　{{formattedDate(reschedulingInfo.target_reservation.date)}}</p>
              <p>時間：　{{reschedulingInfo.target_reservation.start_time.slice(0, -3)}} 〜 {{reschedulingInfo.target_reservation.end_time.slice(0, -3)}}</p>
            </div>
          </div>
          <i
            :class="{'right-arrow': !isNarrowMode, 'down-arrow': isNarrowMode}"
            class="el-icon-back"
          />
          <div
            :class="{'info-inline-block': !isNarrowMode}"
            class="info"
          >
            <h1 class="info-caption">候補日時</h1>
            <ul
              :class="{'narrow-list': !isNarrowMode}"
              id="candidate-list"
              class="info-content">
              <li
                v-for="(candidate, index) in reschedulingInfo.candidate_datetimes"
                :key="'list-item-candidate' + index"
                class="list-item-candidate-index"
              >
                <div>
                  <input
                    :id="'list-item-candidate-radio' + index"
                    v-model="selectedCandidate"
                    type="radio"
                    :value="candidate"
                    :disabled="isDenying"
                    @change="selectedCandidateChanged()"
                  >
                  <label :for="'list-item-candidate-radio' + index">候補{{index+1}}</label>
                </div>
                <div class="candidate-content">
                  <p>日付：{{formattedDate(candidate.date)}}</p>
                  <p>時間：{{candidate.start_time}} 〜 {{candidate.end_time}}{{candidate.mode === 'slot' ? 'の1コマ' : 'の中で' + candidate.duration + '分間' }}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section
        v-if="!isDenying && selectedCandidate && selectedCandidate.mode === 'range'"
        key="timeslot"
        class="section"
      >
        <div>
          <h1 class="section-description">{{selectedCandidate.start_time}} 〜 {{selectedCandidate.end_time}}の中で{{selectedCandidate.duration}}分間、都合の良い時間帯を1つ選んでください</h1>
          <p>（開始時刻か終了時刻のどちらかを指定すると、自動的にもう一方も指定されます）</p>
          <notificationBox
            v-if="isCompletenessChecking && !isTimeslotCompleted"
            key="warning-timeslot"
            class="notificationBox"
            type='warning'
            text="開始時刻と終了時刻を選択してください"
          />
          <notificationBox
            v-else-if="isCompletenessChecking && isTimeslotCompleted"
            key="ok-timeslot"
            class="notificationBox"
            type='ok'
            text="正しく入力されています"
          />
          <el-time-select
            v-model="timeslot.start_time"
            placeholder="開始時刻を選択"
            :picker-options="{
              start: selectedCandidate.start_time,
              end: selectedCandidate.end_time,
              step: '00:05',
              maxTime: moment(selectedCandidate.end_time, 'HH:mm').add(-selectedCandidate.duration + 1, 'minutes').format('HH:mm')
            }"
            style="width: 160px;"
            @change="timeslotStartTimeChanged()"
          />
          <label> 〜 </label>
          <el-time-select
            v-model="timeslot.end_time"
            placeholder="終了時刻を選択"
            :picker-options="{
              start: selectedCandidate.start_time,
              end: selectedCandidate.end_time,
              step: '00:05',
              minTime: moment(selectedCandidate.start_time, 'HH:mm').add(selectedCandidate.duration - 1, 'minutes').format('HH:mm')
            }"
            style="width: 160px;"
            @change="timeslotEndTimeChanged()"
          />
        </div>
      </section>

      <section class="section" v-if='isDenying'>
        <h1 class="section-description">不承認の場合、他の候補日時を複数記入してください。</h1>
        <notificationBox
          v-if="!isAlternativeDatetimes && isCompletenessChecking"
          key="warning-timeslot"
          class="notificationBox"
          type='warning'
          text="開始時刻と終了時刻を選択してください"
        />
        <notificationBox
          v-else-if="isAlternativeDatetimes && isCompletenessChecking"
          key="ok-timeslot"
          class="notificationBox"
          type='ok'
          text="正しく入力されています"
        />
        <div
          key="candidate-list"
        >
          <!--候補日時のリスト-->
          <ul id="candidate-list">
            <li
              v-for="(cand, index) in alternative_datetimes"
              :key="'list-item-candidate' + index"
              class="list-item list-item-candidate"
            >
              <!--1つの候補日時ボックス-->
              <div class="candidate-box">
                <!--ボックスの見出し-->
                <div class="candidate-caption">
                  <div class="candidate-caption-label">
                    <label>候補 {{ index + 1 }}</label>
                  </div>
                  <div class="candidate-caption-button">
                    <el-button
                      class="btn"
                      icon="el-icon-close"
                      :style="{visibility: (alternative_datetimes.length > 1) ? 'visible' : 'hidden'}"
                      @click="deleteCandidate(index)"
                    />
                  </div>
                </div>
                <!--ボックスの見出し 終わり-->
                <!--日付指定部分-->
                <div class="alternative-content">
                  <label>日付：</label>
                  <el-date-picker
                    v-model="cand.date"
                    placeholder="日付を選択"
                    :format="'yyyy年MM月dd日(' + getWeekday(new Date(cand.date)) + ')'"
                    value-format="yyyy-MM-dd"
                    :picker-options="{
                      disabledDate: disabledDate
                    }"
                    style="width: 190px;"
                  />
                </div>
                <!--日付指定部分 終わり-->
                <!--時間指定方法選択部分-->
                <div class="alternative-content">
                  <label>時間：</label>
                  <input
                    :id="'radio-slot-mode' + index"
                    v-model="cand.mode"
                    type="radio"
                    value="slot"
                    @change="cand.start_time = null; cand.end_time = null"
                  >
                  <label :for="'radio-slot-mode' + index">1コマ分を指定</label>
                  <input
                    :id="'radio-range-mode' + index"
                    v-model="cand.mode"
                    type="radio"
                    value="range"
                    @change="cand.start_time = null; cand.end_time = null"
                  >
                  <label :for="'radio-range-mode' + index">範囲で指定</label>
                </div>
                <!--時間指定方法選択部分 終わり-->
                <!--時間指定部分-->
                <div class="alternative-content">
                  <el-time-select
                    v-model="cand.start_time"
                    placeholder="開始時刻を選択"
                    :picker-options="{
                      start: '10:00',
                      end: '23:00',
                      step: '00:05',
                      maxTime: getMaxStartTime(cand)
                    }"
                    style="width: 160px;"
                    @change="candidateStartTimeChanged(cand)"
                  />
                  <label> 〜 </label>
                  <el-time-select
                    v-model="cand.end_time"
                    placeholder="終了時刻を選択"
                    :picker-options="{
                      start: '10:00',
                      end: '23:00',
                      step: '00:05',
                      minTime: getMinEndTime(cand)
                    }"
                    style="width: 160px;"
                    @change="candidateEndTimeChanged(cand)"
                  />
                  <label
                    v-if="cand.mode === 'slot'"
                    key="slot-mode"
                  >
                    の1コマ
                  </label>
                  <label
                    v-else-if="cand.mode === 'range'"
                    key="range-mode"
                  >
                    の中で{{ reservationDuration }}分間
                  </label>
                </div>
                <!--時間指定部分 終わり-->
              </div>
              <!--1つの候補日時ボックス 終わり-->
            </li>
          </ul>
          <!--候補日時のリスト 終わり-->
          <el-button
            class="btn"
            @click="addEmptyCandidate()"
          >
            ＋ 候補日時を追加する
          </el-button>
        </div>
      </section>

      <section
        v-if="$store.state.userInfo.user_type === 'teacher'"
        key="option-section"
        class="section"
      >
        <h1 class="section-description">振替後の授業の情報を指定してください</h1>
        <notificationBox
          v-if="isCompletenessChecking"
          key="ok-reservation-option"
          class="notificationBox"
          type='ok'
          text="正しく入力されています"
        />
        <reschedulingOptionForm :reservationOption="reservationOption" />
      </section>

      <el-button
        v-if="!isDenying"
        class="btn"
        @click="onConfirmButtonClicked()"
      >
        {{ commitMessage }}
      </el-button>
      <el-button
        v-if="isDenying"
        class="btn"
        @click="denyButtonClicked()"
      >
        確認画面に進む
      </el-button>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import NotificationBox from './NotificationBox'
  import ReschedulingOptionForm from './ReschedulingOptionForm'
  import {
    mapActions
  } from 'vuex'

  export default {
    name: 'reschedulingReceiverForm',
    components: {
      'notificationBox': NotificationBox,
      'reschedulingOptionForm': ReschedulingOptionForm
    },
    data () {
      return {
        reschedulingInfo: null,
        windowWidth: document.documentElement.clientWidth,
        selectedCandidate: null,
        isDenying: false,
        commitMessage: '座席を選択する',
        check: 0,
        timeslot: {
          start_time: null,
          end_time: null
        },
        reservationOption: {
          reservationType: '通常',
          hasTest: false,
          reservationMessage: ''
        },
        isCompletenessChecking: false,
        reservationsOnCandidateDates: {},
        alternative_datetimes: [
          {
            date: '',
            mode: 'slot', // 'slot'または'range'
            start_time: '',
            end_time: '',
            duration: null
          }
        ],
        otherReservation: {
          date: '',
          startTime: '',
          endTime: '',
          receiver: 0
        }
      }
    },
    created () {
      this.getReschedulingByHash(this.$route.params.rescheduling_hash)
      .then((res) => {
        if (res) {
          this.reschedulingInfo = res.data
          this.alternative_datetimes[0].duration = this.reschedulingInfo.candidate_datetimes[0].duration
          let candidateDates = []
          for (let cand of res.data.candidate_datetimes) {
            if (candidateDates.indexOf(cand.date) === -1) {
              candidateDates.push(cand.date)
            }
          }
          for (let date of candidateDates) {
            // 予約情報の取得
            this.getReservations({
              date: date,
              school_id: this.$store.state.userInfo.school_id
            })
            .then(() => {
              this.reservationsOnCandidateDates[date] = this.$store.state.reservations
            })
          }
        }
      })
      window.addEventListener('resize', () => {
        this.windowWidth = document.documentElement.clientWidth
      })
    },
    methods: {
      ...mapActions([
        'getReschedulingByHash',
        'getReservations',
        'setReschedulingInfo',
        'updateReschedulingInfo',
        'sendMailReschedulingResult'
      ]),
      moment: moment,
      formattedDate (date) {
        moment.lang('ja', {
          weekdaysShort: ['日', '月', '火', '水', '木', '金', '土']
        })
        return moment(date, 'YYYY-MM-DD').format('YYYY年MM月DD日（ddd）')
      },
      selectedCandidateChanged () {
        if (this.selectedCandidate.mode === 'slot') {
          this.timeslot.start_time = this.selectedCandidate.start_time
          this.timeslot.end_time = this.selectedCandidate.end_time
        } else {
          this.timeslot.start_time = null
          this.timeslot.end_time = null
        }
      },
      timeslotStartTimeChanged () {
        if (this.timeslot.start_time) {
          this.timeslot.end_time = moment(this.timeslot.start_time, 'HH:mm').add(this.selectedCandidate.duration, 'minutes').format('HH:mm')
        } else {
          this.timeslot.end_time = null
        }
      },
      timeslotEndTimeChanged () {
        if (this.timeslot.end_time) {
          this.timeslot.start_time = moment(this.timeslot.end_time, 'HH:mm').add(-this.selectedCandidate.duration, 'minutes').format('HH:mm')
        } else {
          this.timeslot.start_time = null
        }
      },
      onConfirmButtonClicked () {
        if (this.isSelectionCompleted && this.isTimeslotCompleted && !this.isDenying) {
          // ダブルブッキングのチェック
          var date = this.selectedCandidate.date
          this.check = 0
          console.log(this.reschedulingInfo)
          for (var i = 0; i < this.reservationsOnCandidateDates[date].length; i++) {
            if (this.reservationsOnCandidateDates[date][i].student.id === this.reschedulingInfo.receiver.id ||
              this.reservationsOnCandidateDates[date][i].student.id === this.reschedulingInfo.sender.id) {
              if (this.timeslot.start_time <= this.reservationsOnCandidateDates[date][i].end_time && this.timeslot.end_time >= this.reservationsOnCandidateDates[date][i].start_time) {
                if (this.reschedulingInfo.receiver.user_type === 'student') {
                  alert(this.reschedulingInfo.receiver.fullname + '様が同時間帯に他の授業を予約しています')
                } else {
                  alert(this.reschedulingInfo.sender.fullname + '様が同時間帯に他の授業を予約しています')
                }
                this.check = 1
                return
              }
            }
            if (this.reservationsOnCandidateDates[date][i].teacher.id === this.reschedulingInfo.sender.id ||
              this.reservationsOnCandidateDates[date][i].teacher.id === this.reschedulingInfo.receiver.id) {
              if (this.timeslot.start_time <= this.reservationsOnCandidateDates[date][i].end_time && this.timeslot.end_time >= this.reservationsOnCandidateDates[date][i].start_time) {
                if (this.reschedulingInfo.receiver.user_type === 'teacher') {
                  alert(this.reschedulingInfo.receiver.fullname + '様が同時間帯に他の授業を予約しています')
                } else {
                  alert(this.reschedulingInfo.sender.fullname + '様が同時間帯に他の授業を予約しています')
                }
                this.check = 1
                return
              }
            }
          }
          if (this.check === 0) {
            // 選択した日時情報をオブジェクトに代入
            this.reschedulingInfo.rescheduled_reservation.date = this.selectedCandidate.date
            this.reschedulingInfo.rescheduled_reservation.start_time = this.timeslot.start_time
            this.reschedulingInfo.rescheduled_reservation.end_time = this.timeslot.end_time
            // receiver が先生の場合、授業オプションの情報を代入する
            if (this.reschedulingInfo.receiver.user_type === 'teacher') {
              this.reschedulingInfo.reservationOption = this.reservationOption
              this.reschedulingInfo.rescheduled_reservation.has_test = this.reservationOption.hasTest
              this.reschedulingInfo.rescheduled_reservation.reservation_message = this.reservationOption.reservationMessage
              this.reschedulingInfo.rescheduled_reservation.reservation_type = this.reservationOption.reservationType
            }
            // 振替拒否の有無の設定をオブジェクトに代入
            this.reschedulingInfo.isDenying = this.isDenying
            console.log(this.reschedulingInfo)
            // 日時情報をstoreに保存
            this.setReschedulingInfo(this.reschedulingInfo)
            // 教室選択ページにジャンプ
            this.$router.push('/rescheduling/' + this.$route.params.rescheduling_hash + '/ReschedulingReceiverClassroom')
          }
        } else {
          this.isCompletenessChecking = true
          alert('入力内容にエラーがあります！')
          window.scrollTo(0, 0)
        }
      },
      denyButtonClicked () {
        if (this.isDenying && this.isAlternativeDatetimes) {
          this.reschedulingInfo.isDenying = this.isDenying
          this.reschedulingInfo.alternative_datetimes = this.alternative_datetimes
          if (this.reschedulingInfo.receiver.user_type === 'teacher') {
            this.reschedulingInfo.reservationOption = this.reservationOption
            this.reschedulingInfo.rescheduled_reservation.has_test = this.reservationOption.hasTest
            this.reschedulingInfo.rescheduled_reservation.reservation_message = this.reservationOption.reservationMessage
            this.reschedulingInfo.rescheduled_reservation.reservation_type = this.reservationOption.reservationType
          }
          this.setReschedulingInfo(this.reschedulingInfo).then(() => {
            console.log(this.reschedulingInfo)
            this.$router.push('/rescheduling/' + this.$route.params.rescheduling_hash + '/confirmation_rcvr')
          })
          // this.updateReschedulingInfo({
          //  hash: this.reschedulingInfo.hash,
          //  reservation: {
          //    is_accepted: false,
          //    rescheduled_reservation: this.reschedulingInfo.target_reservation.id,
          //    receiver_message: this.reschedulingInfo.receiver_message
          //  }
          // }).then(() => {
          //  this.sendMailReschedulingResult(this.reschedulingInfo.hash)
          // }).then(() => {
          //  this.$router.push('/rescheduling/' + this.$route.params.rescheduling_hash + '/final_confirmation')
          // })
        } else {
          this.isCompletenessChecking = true
          alert('入力内容にエラーがあります！')
          window.scrollTo(0, 0)
        }
      },
      deleteCandidate (index) {
        this.alternative_datetimes.splice(index, 1)
      },
      getWeekday (date) {
        return ['日', '月', '火', '水', '木', '金', '土'][date.getDay()]
      },
      disabledDate (date) {
        // 予約開始は本日より 24H 後から 2 ヶ月先まで
        let nowdt = new Date()
        nowdt.setDate(nowdt.getDate() + 1)
        let stdt = new Date(nowdt.getFullYear(), nowdt.getMonth(), nowdt.getDate(), 0, 0, 0)
        let enddt = new Date()
        enddt.setMonth(enddt.getMonth() + 1)
        return date < stdt || (!this.$store.state.userInfo.is_Sunday_user && date.getDay() === 0) || date > enddt
      },
      getMaxStartTime (cand) {
        if (cand.mode === 'slot') {
          return moment('23:00', 'HH:mm').add(-this.reservationDuration + 1, 'minutes').format('HH:mm')
        } else if (cand.mode === 'range') {
          return moment(cand.end_time ? cand.end_time : '23:00', 'HH:mm').add(-this.reservationDuration + 1, 'minutes').format('HH:mm')
        } else {
          return ''
        }
      },
      getMinEndTime (cand) {
        if (cand.mode === 'slot') {
          return moment('10:00', 'HH:mm').add(this.reservationDuration - 1, 'minutes').format('HH:mm')
        } else if (cand.mode === 'range') {
          return moment(cand.start_time ? cand.start_time : '10:00', 'HH:mm').add(this.reservationDuration - 1, 'minutes').format('HH:mm')
        } else {
          return ''
        }
      },
      candidateStartTimeChanged (cand) {
        if (cand.mode === 'slot') {
          if (cand.start_time) {
            cand.end_time = moment(cand.start_time, 'HH:mm').add(this.reservationDuration, 'minutes').format('HH:mm')
          } else {
            cand.end_time = null
          }
        }
      },
      candidateEndTimeChanged (cand) {
        if (cand.mode === 'slot') {
          if (cand.end_time) {
            cand.start_time = moment(cand.end_time, 'HH:mm').add(-this.reservationDuration, 'minutes').format('HH:mm')
          } else {
            cand.start_time = null
          }
        }
      },
      addEmptyCandidate () {
        this.alternative_datetimes.push({
          date: '',
          mode: 'slot',
          start_time: '',
          end_time: '',
          duration: this.reservationDuration
        })
        this.$nextTick(() => {
          let list = document.getElementById('candidate-list')
          console.log(list)
          list.scrollTop = list.scrollHeight
        })
      }
    },
    computed: {
      senderDisplayName () {
        return this.reschedulingInfo.sender.fullname +
          (this.reschedulingInfo.sender.user_type === 'teacher' ? '先生' : '様')
      },
      receiverDisplayName () {
        return this.reschedulingInfo.receiver.fullname +
          (this.reschedulingInfo.receiver.user_type === 'teacher' ? '先生' : '様')
      },
      isNarrowMode () {
        return this.windowWidth < 670
      },
      isSelectionCompleted () {
        return this.isDenying || this.selectedCandidate !== null
      },
      isTimeslotCompleted () {
        return this.isDenying || this.selectedCandidate.mode === 'slot' || (this.timeslot.start_time && this.timeslot.end_time)
      },
      isAlternativeDatetimes () {
        for (let candidate of this.alternative_datetimes) {
          if (!candidate.date || !candidate.start_time || !candidate.end_time) {
            return false
          }
        }
        return true
      },
      checkExpireDate () {
        let regdate = this.reschedulingInfo.registered_datetime.split(' ')
        let date = regdate[0].split('-')
        let time = regdate[1].split(':')
        if (date[1] !== '10') {
          date[1] = date[1].replace('0', '')
        }
        date[1] = Number(date[1]) - 1
        let scheduledDate = new Date(date[0], date[1], date[2], time[0], time[1], time[2])
        scheduledDate.setDate(scheduledDate.getDate() + 1)
        return new Date() < scheduledDate
      },
      reservationDuration () {
        let calcMinutesDiff = (t1, t2) => {
          let tmpDate = '2018-01-01' // いつでもいい
          return (new Date(tmpDate + 'T' + t1).getTime() - new Date(tmpDate + 'T' + t2).getTime()) / 1000 / 60
        }
        if (this.reschedulingInfo.target_reservation && !this.isOther) {
          return calcMinutesDiff(this.reschedulingInfo.target_reservation.end_time.slice(0, -3), this.reschedulingInfo.target_reservation.start_time.slice(0, -3))
        } else if (this.isOther && this.otherReservation.date && this.otherReservation.startTime && this.otherReservation.endTime) {
          return calcMinutesDiff(this.otherReservation.endTime, this.otherReservation.startTime)
        } else {
          return 0
        }
      }
    },
    watch: {
      isDenying: function () {
        if (this.isDenying) {
          this.commitMessage = '確認画面へ'
        } else {
          this.commitMessage = '座席を選択する'
        }
      }
    }
  }
</script>
<style lang="css" scoped>
  #reschedulingReceiverForm {
    text-align: center;
    font-family: 'Avenir','Helvetica Neue','Helvetica','Arial','Hiragino Sans','ヒラギノ角ゴシック',YuGothic,'Yu Gothic','メイリオ', Meiryo,'ＭＳ Ｐゴシック','MS PGothic', 'sans-serif';
    margin-bottom: 50px;
  }
  .header {
    background-color: #0065B3;
    color: white;
    text-align: center;
    font-size: 24px;
    margin: 10px auto;
    max-width: 500px;
  }
  .section {
    border-bottom: solid 2px black;
    text-align: center;
    margin: 10px 5% 10px 5%;
    padding-bottom: 20px;
  }
  .section-description {
    font-size: 18px;
    margin-bottom: 5px;
  }
  .info {
    max-width: 500px;
    margin: 10px auto;
    border: solid 1px #0065b3;
  }
  .info-caption {
    font-size: 16px;
    color: white;
    background-color: #0065b3;
  }
  .info-content {
    text-align: left;
    padding: 10px 10px 10px 10px;
  }
  .reason-row {
    margin: 0 20px;
  }
  .info-inline-block {
    display: inline-block;
    vertical-align: middle;
  }
  .el-icon-back {
    font-size: 32px;
    font-weight: bold;
    color: #0065b3;
    vertical-align: middle;
  }
  .right-arrow {
    transform: rotate(180deg);
  }
  .down-arrow {
    transform: rotate(270deg);
  }
  #reservation-list, #candidate-list {
    text-align: center;
    overflow: auto;
    max-height: 320px;
    max-width: 500px;
    margin: 10px auto 10px auto;
  }
  .narrow-list {
    max-width: 300px;
  }
  .list-item-candidate {
    padding-left: 5px;
    border-bottom: solid 1px gray;
  }
  .candidate-content {
    padding: 0 10px;
  }
  #message-area {
    max-width: 500px;
    margin: auto;
  }
  .btn {
    margin: 5px 0 5px 0;
  }
  .notificationBox {
    text-align: left;
    max-width: 500px;
    margin: 5px auto;
  }
  .candidate-box {
    border: solid 2px #0065b3;
    margin: 10px 0 10px 0;
  }
  .candidate-caption {
    background-color: #0065b3;
    color: white;
    font-size: 18px;
    padding-left: 1%;
    padding-right: 1%;
  }
  .candidate-caption-label {
    display: inline-block;
    text-align: left;
    width: 20%;
    margin: auto;
  }
  .candidate-caption-button {
    display: inline-block;
    text-align: right;
    width: 78%;
  }
  .alternative-content {
    margin: 5px 0 5px 3%;
  }
  .list-item-candidate-index{
    padding-left: 5px;
    border-bottom: solid 1px gray;
    text-align: left;
  }
</style>



// WEBPACK FOOTER //
// ReschedulingReceiverForm.vue?0556761f